import axios, { AxiosResponse } from 'axios';
import _ from 'underscore';

import { Favorites } from '~src/favorites/resources/Favorites';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { ListResult } from '~src/ts/fetchers/ListResult';
import { CollectionMapper } from '~src/ts/mappers/CollectionMapper';
import { OrderKeys } from '~src/ts/models/OrderKeys';
import { BaseParamsBuilderResourceQuery } from '~src/ts/resources/ResourceQuery';
import { IFavorite } from '~src/ts/ServerApi/types/IFavorite';
import { FilterOps, ILeafFilterClause, IStringValuedFilterClause } from '~src/ts/ServerApi/types/IFilterClause';
import { IOrderClause } from '~src/ts/ServerApi/types/IOrderClause';

import { AddRemoveCollectionItem, AddRemoveCollectionItemsData, CollectionContentItem, CollectionError, CollectionItem } from '../space/CollectionModels';
import { COLLECTIONS_PATH, collectionsAxiosRequestConfig, CollectionsListResponse, getFilterUrlParam } from './CollectionResourceQueryHelpers';

export interface GetCollectionItemsResponse extends CollectionsListResponse<CollectionItem> {}

export interface AddRemoveItemsResponse {
  readonly response: number;
  readonly errors: CollectionError[];
}

export interface CollectionItemsQueryParams {
  search?: string;
  sort?: string;
  filter?: string;
  start_index?: number;
  max_items?: number;
}

export interface ItemReordering {
  itemId: string;
  itemType: string;
  itemIdToMoveAfter: string;
  itemTypeToMoveAfter: string;
}

const mapToCrassAndCollectionSpecificSortField: { [key: string]: string } = {
  [OrderKeys.contentTypeOrder]: 'type',
  [OrderKeys.dateAdded]: 'date_added',
  [OrderKeys.name]: 'title',
  [OrderKeys.updatedAt]: 'modifiedTime',
  [OrderKeys.workbookName]: 'containerName',
  [OrderKeys.index]: 'sheetNumber',
  [OrderKeys.numSheets]: 'sheetCount',
  [OrderKeys.lastRefreshedAt]: 'extractUpdatedAt',
  [OrderKeys.hitsLastOneMonthTotal]: 'hitsSmallSpanTotal',
  [OrderKeys.hitsLastThreeMonthsTotal]: 'hitsMediumSpanTotal',
  [OrderKeys.hitsLastTwelveMonthsTotal]: 'hitsLargeSpanTotal',
  [OrderKeys.projectName]: 'containerName',
  [OrderKeys.createdAt]: 'createdTime',
};

// add a call to orderCollections here TFS1302371
export class CollectionItemsResourceQuery extends BaseParamsBuilderResourceQuery<CollectionContentItem> {

  public constructor(private collectionLuid: string) {
    super();
  }

  public getSlice(start: number, size: number): Promise<ListResult<CollectionContentItem>> {
    // don't need to fetch favorites if "size" is 0 since we're just getting the counts and don't need to show whether
    // an item is favorited or not. We get 1000 since no user really has that many favorites.
    const favoritesPromise = size > 0 ? Favorites.fetchFromGetFavorites().getSlice(0, 1000) : Promise.resolve(ListResult.empty());

    let queryParams: CollectionItemsQueryParams = {};
    let filters: ILeafFilterClause[] = this.getFilters();
    const matchesFilter = filters.find((clause) => clause.operator === FilterOps.matches);
    if (matchesFilter) {
      const searchTerm = encodeURIComponent((matchesFilter as IStringValuedFilterClause).value);
      queryParams.search = searchTerm;
    }

    const filterParams = getFilterUrlParam(this.getFilters());

    if (filterParams) {
      queryParams.filter = filterParams;
    }

    const order: IOrderClause[] = this.getOrder();
    const orderClause = order
      .map((clause) => (mapToCrassAndCollectionSpecificSortField[clause.field] || clause.field)
        + ' ' + (clause.ascending ? 'asc' : 'desc'))
      .join(',');

    if (orderClause) {
      queryParams.sort = orderClause;
    }

    if (start != null) {
      queryParams.start_index = start;
    }

    if (size != null) {
      queryParams.max_items = size;
    }


    let queryString = '';
    if (FeatureFlags.collectionsGAEnabled && !_.isEmpty(queryParams)) {
      queryString = '?';
      queryString += _.pairs(queryParams).map((value) => value[0] + '=' + value[1]).join('&');
    }

    const url = COLLECTIONS_PATH + '/' + this.collectionLuid + '/items' + queryString;
    const requestConfig = collectionsAxiosRequestConfig('GET', url);
    const itemsPromise = axios.request<GetCollectionItemsResponse>(requestConfig);

    return Promise.all([itemsPromise, favoritesPromise]).then((result: [AxiosResponse<GetCollectionItemsResponse>, ListResult<IFavorite>]) => {
      const favorites: _.Dictionary<IFavorite> = _.indexBy(result[1].items, 'objectLuid');
      return CollectionMapper.mapItemsList(result[0], start, size, favorites);
    }).catch((e) => Promise.reject(e.response));
  }

  public getCount(): Promise<number> {
    return this.getSlice(0, 0).then((slice: ListResult<CollectionContentItem>) => slice.total);
  }

  public getFirst(): Promise<CollectionContentItem> {
    throw new Error('CollectionItemsResourceQuery getFirst not implemented');
  }

  public addItemsToCollection(items: IContentInfo[]): Promise<AddRemoveCollectionItemsData> {
    let collectionItems: AddRemoveCollectionItem[] = [];
    for (let item of items) {
      collectionItems.push({
        contentLuid: item.luid,
        contentType: item.contentType,
        contentName: item.name,
      });
    }

    const url = COLLECTIONS_PATH + '/' + this.collectionLuid + '/items/batchCreate';
    const requestConfig = collectionsAxiosRequestConfig('POST', url, { items: collectionItems });

    return axios.request<AddRemoveItemsResponse>(requestConfig).then((response: AxiosResponse<AddRemoveItemsResponse>) => {
      return CollectionMapper.mapAddRemoveItemsResponse(response, this.collectionLuid);
    }).catch((e) => Promise.reject(e.response));
  }

  public reorderCollectionItems(orderings: ItemReordering[]): Promise<IResult> {
    // TODO: Add a response model if needed once its finalized.
    const url = COLLECTIONS_PATH + '/' + this.collectionLuid + '/orderItems';
    const requestConfig = collectionsAxiosRequestConfig('PATCH', url, { itemOrderings: orderings });
    return axios.request(requestConfig);
  }

  public static readonly ContentFiltersPerRemoveCall = 30;
  public removeItemsFromCollection(items: IContentInfo[]): Promise<AddRemoveCollectionItemsData> {
    const contentItemsPromises  = [];
    for (let start = 0; start < items.length; start += CollectionItemsResourceQuery.ContentFiltersPerRemoveCall) {
      const contentItemsToFilter = items.slice(start, start + CollectionItemsResourceQuery.ContentFiltersPerRemoveCall);
      contentItemsPromises.push(this.removeItemsFromCollectionImpl(contentItemsToFilter));
    }

    return Promise.all(contentItemsPromises).then((response) => this.reduceAddRemoveCollectionsItemsData(response));
  }

  private removeItemsFromCollectionImpl(items: IContentInfo[]): Promise<AddRemoveCollectionItemsData> {
    let collectionItems: AddRemoveCollectionItem[] = [];
    for (let item of items) {
      collectionItems.push({
        contentLuid: item.luid,
        contentName: null,
        contentType: item.contentType,
      });
    }

    const url = COLLECTIONS_PATH + '/' + this.collectionLuid + '/items/batchDelete';
    const requestConfig = collectionsAxiosRequestConfig('POST', url, { items: collectionItems });

    return axios.request<AddRemoveItemsResponse>(requestConfig).then((response: AxiosResponse<AddRemoveItemsResponse>) => {
      return CollectionMapper.mapAddRemoveItemsResponse(response, this.collectionLuid);
    }).catch((e) => Promise.reject(e.response));
  }

  public reduceAddRemoveCollectionsItemsData(items: AddRemoveCollectionItemsData[]): AddRemoveCollectionItemsData {
    const initial: AddRemoveCollectionItemsData = {numItemsSuccessfullyUpdated: 0, errors: [], collectionLuid: this.collectionLuid};
    return items.reduce((previousValue, currentValue) => {
      return {
        numItemsSuccessfullyUpdated: currentValue.numItemsSuccessfullyUpdated + previousValue.numItemsSuccessfullyUpdated,
        errors: [...currentValue.errors, ...previousValue.errors],
        collectionLuid: this.collectionLuid
      };
    }, initial);
  }
}
